import { useEffect } from 'react';
import lodash from 'lodash';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import {
  FINANCIAL_INFO_TABS_LIST,
  FINANCIAL_INFO_ROWS,
  PROVIDER_SOURCES,
  DEFAULT_UNDEFINED_EMPTY_VALUE,
  DEFAULT_EMPTY_VALUE,
  DEFAULT_PREV_FINANCIAL_LIMIT,
  FINANCIAL_INFO_DNB_NUMBER_FIELDS,
  FINANCIAL_INFO_DEFAULT_NUMBER_FIELDS,
} from '../../../constants/companyReport';
import { v4 as uuidv4 } from 'uuid';
import { formatDate, formatNumber } from 'src/services/companyReportDetail.service';

interface Props {
  activeIndex: string;
  handleOnClick: (index: string) => void;
  detailData: any;
}

const FinancialInformation = ({ activeIndex, handleOnClick, detailData }: Props) => {
  let defaultFinancialInformation =
    detailData?.companyProfile?.companyProfile?.financialInformation &&
    Array.isArray(detailData?.companyProfile?.companyProfile?.financialInformation)
      ? detailData.companyProfile.companyProfile.financialInformation.slice(0, 3)
      : [];

  // combine latestFinancials and previousFinancials to one array so that we reduce doing the same thing on the view
  let dnbFinancialInformationV2 = [].concat(
    detailData?.companyProfile?.companyProfile?.financialInformationV2?.latestFinancials,
    detailData?.companyProfile?.companyProfile?.financialInformationV2?.previousFinancials?.slice(
      0,
      DEFAULT_PREV_FINANCIAL_LIMIT,
    ),
  );

  return (
    <div className={`card accordion ${activeIndex === 'financial-info' ? 'active' : ''}`}>
      <div className="accordion-heading" onClick={() => handleOnClick('financial-info')}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-4 align-items-center">
            <h4 className="mb-0">FINANCIAL INFORMATION</h4>
          </div>
          <div>
            <img
              className="chevron-icon"
              src={require('src/assets/images/icons/chevron-down.svg').default}
              alt="chevron-down icon"
            />
          </div>
        </div>
      </div>
      <div className="accordion-content">
        {detailData?.source === PROVIDER_SOURCES.DNB ? (
          // we will need for next version
          // <DnbFinancialInformationDetails financialInformation={dnbFinancialInformation} />
          <DnbFinancialInformationV2Details financialInformation={dnbFinancialInformationV2} />
        ) : (
          <DefaultFinancialInformationDetails financialInformation={defaultFinancialInformation} />
        )}
      </div>
    </div>
  );
};

function DefaultFinancialInformationDetails({ financialInformation }: any) {
  if (!Array.isArray(financialInformation)) {
    financialInformation = [];
  }

  useEffect(() => {
    if (financialInformation?.length) {
      for (let propertName of FINANCIAL_INFO_DEFAULT_NUMBER_FIELDS) {
        for (let i = 0; i < financialInformation?.length; i++) {
          let value = lodash.get(financialInformation[i], propertName.split('.'));
          if (value && !value?.toString().includes(',')) {
            lodash.set(financialInformation[i], propertName.split('.'), formatNumber(value));
          }
        }
      }
    }
  }, [financialInformation]);

  return (
    <Tabs>
      <TabList>
        <Tab>{FINANCIAL_INFO_TABS_LIST.PROFIT_LOSS}</Tab>
        <Tab>{FINANCIAL_INFO_TABS_LIST.BALANCE_SHEET}</Tab>
        <Tab>{FINANCIAL_INFO_TABS_LIST.RATIOS}</Tab>
        <Tab>{FINANCIAL_INFO_TABS_LIST.OTHER_FINANCIALS}</Tab>
      </TabList>

      <TabPanel>
        <div className="mt-4">
          <table className="accordion-table">
            <tbody>
              <tr>
                <th scope="col">Financial Year</th>

                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.yearEndDate || DEFAULT_EMPTY_VALUE}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Number of Weeks</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.numberOfWeeks || DEFAULT_EMPTY_VALUE}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Currency</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.currency || DEFAULT_EMPTY_VALUE}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Consolidated Accounts</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.consolidatedAccounts === true ? 'Yes' : DEFAULT_EMPTY_VALUE}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Revenue</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.revenue}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Operating Costs</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.profitAndLoss?.operatingCost || DEFAULT_UNDEFINED_EMPTY_VALUE}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Operating Profit</th>

                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.operatingProfit}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Wage & Salaries</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.wagesAndSalaries}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Pension Costs</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.pensionCosts}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Depreciation</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.depreciation}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Amortisation</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.amortisation}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Financial Income</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.profitAndLoss?.financialIncome || DEFAULT_UNDEFINED_EMPTY_VALUE}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Financial Expenses</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.financialExpenses}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Extraordinary Income</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.profitAndLoss?.extraordinaryIncome ||
                      DEFAULT_UNDEFINED_EMPTY_VALUE}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Extraordinary Costs</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.profitAndLoss?.extraordinaryIncome ||
                      DEFAULT_UNDEFINED_EMPTY_VALUE}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Profit before Tax</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.profitBeforeTax}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Tax</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.tax}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Profit after Tax</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.profitAfterTax}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Dividends</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.dividends}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Minority Interests</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.minorityInterests}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Other Appropriations</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.otherAppropriations}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Retained Profit</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.profitAndLoss?.retainedProfit}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </TabPanel>

      <TabPanel>
        <div className="mt-4">
          <table className="accordion-table">
            <tbody>
              <tr>
                <th scope="col">Financial Year</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.yearEndDate || DEFAULT_EMPTY_VALUE}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Number of Weeks</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.numberOfWeeks || DEFAULT_EMPTY_VALUE}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Currency</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.currency || DEFAULT_EMPTY_VALUE}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Consolidated Accounts</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.consolidatedAccounts || DEFAULT_EMPTY_VALUE}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Tangible Assets</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.totalTangibleAssets}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Intangible Assets</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.totalIntangibleAssets}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Other Fixed Assets</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.totalOtherFixedAssets}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Fixed Assets</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.totalFixedAssets}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Inventories</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.totalInventories}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Trade Receivables</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.tradeReceivables}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Miscellaneous Receivables</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.miscellaneousReceivables}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Receivables</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.totalReceivables}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Cash</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.cash}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Other Current Assets</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.otherCurrentAssets}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Current Assets</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.totalCurrentAssets}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Assets</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.totalAssets}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Trade Payables</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.tradePayables}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Bank Liabilities</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.bankLiabilities}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Other Loans Or Finance</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.otherLoansOrFinance}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Miscellaneous Liabilities</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.miscellaneousLiabilities}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Current Liabilities</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.totalCurrentLiabilities}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Bank Liabilities Due After 1 Year</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.balanceSheet?.bankLiabilitiesDueAfter1Year}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Other Loans Or Finance Due After 1 Year</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.balanceSheet?.otherLoansOrFinanceDueAfter1Year}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Miscellaneous Liabilities Due After 1 Year</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.balanceSheet?.miscellaneousLiabilitiesDueAfter1Year}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Long Term Liabilities</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.totalLongTermLiabilities}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Liabilities</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.totalLiabilities}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Called Up Share Capital</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.calledUpShareCapital}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Revenue Reserves</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.revenueReserves}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Other Reserves</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.otherReserves}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Share holders Equity</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.balanceSheet?.totalShareholdersEquity}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </TabPanel>

      <TabPanel>
        <div className="mt-4">
          <table className="accordion-table">
            <tbody>
              <tr>
                <th scope="row">Current Debt Ratio</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.ratios?.currentDebtRatio || DEFAULT_EMPTY_VALUE}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Gearing</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.ratios?.gearing || DEFAULT_EMPTY_VALUE}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Equity In Percentage</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.ratios?.equityInPercentage || DEFAULT_EMPTY_VALUE}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Total Debt Ratio</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.ratios?.totalDebtRatio || DEFAULT_EMPTY_VALUE}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </TabPanel>

      <TabPanel>
        <div className="mt-4">
          <table className="accordion-table">
            <tbody>
              <tr>
                <th scope="row">contingentLiabilities</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>
                    {financialInfo?.otherFinancials?.contingentLiabilities || DEFAULT_EMPTY_VALUE}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">workingCapital</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.otherFinancials?.workingCapital}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Equity In Percentage</th>
                {financialInformation.map((financialInfo: any) => (
                  <td key={uuidv4()}>{financialInfo?.otherFinancials?.netWorth}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </TabPanel>
    </Tabs>
  );
}

function DnbFinancialInformationV2Details({ financialInformation }: any) {
  useEffect(() => {
    if (financialInformation?.length) {
      for (let propertName of FINANCIAL_INFO_DNB_NUMBER_FIELDS) {
        for (let i = 0; i < financialInformation?.length; i++) {
          let value = lodash.get(financialInformation[i], propertName.split('.'));
          if (value && !value?.toString().includes(',')) {
            lodash.set(financialInformation[i], propertName.split('.'), formatNumber(value));
          }
        }
      }
    }
  }, [financialInformation]);

  return (
    <table className="accordion-table">
      <tbody>
        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.consolidated}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>
              {formatDate(info?.financialStatementToDate)} {info.units ? `(${info?.units})` : ''}
            </td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.currency}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.currency || DEFAULT_EMPTY_VALUE}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.period}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.financialStatementDuration || DEFAULT_EMPTY_VALUE}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.actualEstimate}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.reliability?.description || DEFAULT_EMPTY_VALUE}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.audited}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>
              {info?.isAudited === undefined
                ? DEFAULT_EMPTY_VALUE
                : (info?.isAudited ? 'True' : 'False') || DEFAULT_EMPTY_VALUE}
            </td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.turnoverSales}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.salesRevenue}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.profitLossBeforeTax}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.profitBeforeTaxes}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.grossIncome}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.grossProfit}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.netIncome}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.netIncome}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.shareholderFunds}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.netWorth}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.totalAssets}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.totalAssets}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.totalFixedAssets}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.fixedAssets}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.totalCurrentAssets}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.totalCurrentAssets}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.cashAndLiquidAssets}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.cashAndLiquidAssets}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.totalLiabilities}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.totalLiabilities}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.totalLongTermLiabilities}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.totalLongTermLiabilities}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.totalCurrentLiabilities}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.totalCurrentLiabilities}</td>
          ))}
        </tr>

        <tr>
          <th scope="col">{FINANCIAL_INFO_ROWS.totalLiabilitiesAndEquities}</th>
          {financialInformation.map((info: any) => (
            <td key={uuidv4()}>{info?.overview?.totalLiabilitiesEquity}</td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

export default FinancialInformation;
